import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { IMedication } from '../../../../pages/common';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  ITreatmentPlanV3MedicationPage,
  CreateTreatmentPlanV3MedicationPayload,
  UpdateTreatmentPlanV3MedicationPayload,
  DeleteTreatmentPlanV3MedicationPayload,
  DeleteAllTreatmentPlanV3MedicationsPayload,
  SetTreatmentPlanV3NoMedicationPayload,
  TreatmentPlanV3MedicationThunks,
  MedicationMutationAction,
} from './types';

export function addTreatmentPlanV3MedicationCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  addGetTreatmentPlanV3MedicationPageCases(builder, thunks);
  addCreateTreatmentPlanV3MedicationCases(builder, thunks);
  addUpdateTreatmentPlanV3MedicationCases(builder, thunks);
  addDeleteTreatmentPlanV3MedicationCases(builder, thunks);
  addDeleteAllTreatmentPlanV3MedicationsCases(builder, thunks);
  addSetTreatmentPlanV3NoMedicationCases(builder, thunks);
}

function isForLoadedMedicationPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.medicationPageTreatmentPlanId;
}

function markMedicationPageChanged(state: TreatmentPlanV3State) {
  if (state.medicationPageLoading) {
    state.medicationPageChangedSinceRequest = true;
  }
}

function addGetTreatmentPlanV3MedicationPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  const { getTreatmentPlanV3MedicationPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3MedicationPage.pending, (state, action) => {
      // A reload of the same plan keeps the current page; only switching plans discards it.
      if (!isForLoadedMedicationPage(state, action)) {
        state.medicationPage = null;
      }
      state.medicationPageLoading = true;
      state.medicationPageError = null;
      state.medicationPageRequestId = action.meta.requestId;
      state.medicationPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.medicationPageChangedSinceRequest = false;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3MedicationPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.medicationPageRequestId) return;
      state.medicationPageLoading = false;
      state.medicationPageError = null;
      // A response built before an in-flight save would roll that save back.
      if (!state.medicationPageChangedSinceRequest || !state.medicationPage) {
        state.medicationPage = action.payload;
      }
      state.medicationPageChangedSinceRequest = false;
    })
    .addCase(getTreatmentPlanV3MedicationPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.medicationPageRequestId) return;
      state.medicationPageLoading = false;
      state.medicationPageError = action.error;
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3MedicationCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  const { createTreatmentPlanV3Medication } = thunks;
  const flag = 'createMedicationLoading';

  builder
    .addCase(createTreatmentPlanV3Medication.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3Medication.fulfilled,
      (
        state,
        action: MedicationMutationAction<IMedication, CreateTreatmentPlanV3MedicationPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedMedicationPage(state, action) || !state.medicationPage) return;
        markMedicationPageChanged(state);
        state.medicationPage.medications.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3Medication.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedMedicationPage(state, action)) return;
      state.error = action.error;
    });
}

function addUpdateTreatmentPlanV3MedicationCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  const { updateTreatmentPlanV3Medication } = thunks;
  const flag = 'updateMedicationLoading';

  builder
    .addCase(updateTreatmentPlanV3Medication.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3Medication.fulfilled,
      (
        state,
        action: MedicationMutationAction<IMedication, UpdateTreatmentPlanV3MedicationPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedMedicationPage(state, action) || !state.medicationPage) return;
        markMedicationPageChanged(state);
        const list = state.medicationPage.medications;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3Medication.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedMedicationPage(state, action)) return;
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3MedicationCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  const { deleteTreatmentPlanV3Medication } = thunks;
  const flag = 'deleteMedicationLoading';

  builder
    .addCase(deleteTreatmentPlanV3Medication.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3Medication.fulfilled,
      (state, action: MedicationMutationAction<void, DeleteTreatmentPlanV3MedicationPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedMedicationPage(state, action) || !state.medicationPage) return;
        markMedicationPageChanged(state);
        state.medicationPage.medications = state.medicationPage.medications.filter(
          (row) => row.id !== action.meta.arg.medicationId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3Medication.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedMedicationPage(state, action)) return;
      state.error = action.error;
    });
}

function addDeleteAllTreatmentPlanV3MedicationsCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  const { deleteAllTreatmentPlanV3Medications } = thunks;
  const flag = 'deleteAllMedicationsLoading';

  builder
    .addCase(deleteAllTreatmentPlanV3Medications.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    // `no_medication` is not flipped here — it comes from the follow-up PATCH response.
    .addCase(
      deleteAllTreatmentPlanV3Medications.fulfilled,
      (
        state,
        action: MedicationMutationAction<void, DeleteAllTreatmentPlanV3MedicationsPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedMedicationPage(state, action) || !state.medicationPage) return;
        markMedicationPageChanged(state);
        state.medicationPage.medications = [];
      },
    )
    .addCase(deleteAllTreatmentPlanV3Medications.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedMedicationPage(state, action)) return;
      state.error = action.error;
    });
}

function addSetTreatmentPlanV3NoMedicationCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3MedicationThunks,
) {
  const { setTreatmentPlanV3NoMedication } = thunks;
  const flag = 'setNoMedicationLoading';

  builder
    .addCase(setTreatmentPlanV3NoMedication.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      setTreatmentPlanV3NoMedication.fulfilled,
      (
        state,
        action: MedicationMutationAction<
          ITreatmentPlanV3MedicationPage,
          SetTreatmentPlanV3NoMedicationPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedMedicationPage(state, action)) return;
        markMedicationPageChanged(state);
        state.medicationPage = action.payload;
      },
    )
    .addCase(setTreatmentPlanV3NoMedication.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedMedicationPage(state, action)) return;
      state.error = action.error;
    });
}
